import { useTranslation } from 'react-i18next';
import type { NetworkCostSummaryDto } from '@shared/api/contracts';
import { costStateLabel } from '@entities/reconciliation';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { TypographyMuted } from '@shared/ui';

interface NetworkCostSummaryProps {
  assetCode: string;
  withdrawalAmount: string;
  summary: NetworkCostSummaryDto;
  className?: string;
}

export function NetworkCostSummary({ assetCode, withdrawalAmount, summary, className }: NetworkCostSummaryProps) {
  const { t } = useTranslation('cabinet');
  const chargeAsset = summary.chargeAsset || assetCode;

  const row = (label: string, value: string | null, hint?: string) => (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="min-w-0 text-end">
        <span className="break-all font-mono text-sm tabular-nums text-foreground">
          {value ?? t('balance.amountUnknown')}
        </span>
        {hint ? <TypographyMuted className="mt-0.5 text-xs">{hint}</TypographyMuted> : null}
      </div>
    </div>
  );

  return (
    <div className={className ?? 'surface-card space-y-3 p-3 sm:p-4'}>
      <p className="text-sm font-medium text-foreground">{t('networkCost.title')}</p>
      {row(
        t('networkCost.payer'),
        t(`networkCost.payer.${summary.payer}`, { defaultValue: summary.payer }),
      )}
      {row(t('networkCost.withdrawalAmount'), formatLedgerAmount(withdrawalAmount, { assetLabel: assetCode }))}
      {row(
        t('networkCost.recipientAmount'),
        formatLedgerAmount(summary.netPayoutAmount, { assetLabel: assetCode }),
      )}
      {row(
        t('networkCost.estimate'),
        formatLedgerAmount(summary.expectedWithBuffer, { assetLabel: chargeAsset }),
      )}
      {row(
        t('networkCost.charged'),
        formatLedgerAmount(summary.charged, { assetLabel: chargeAsset }),
      )}
      {summary.actual
        ? row(t('networkCost.actual'), formatLedgerAmount(summary.actual, { assetLabel: chargeAsset }))
        : row(t('networkCost.actual'), null, t('networkCost.actualPending'))}
      {summary.adjustment && summary.adjustment !== '0'
        ? row(t('networkCost.adjustment'), formatLedgerAmount(summary.adjustment, { assetLabel: chargeAsset }))
        : null}
      {row(t('networkCost.state'), costStateLabel(t, String(summary.costState)))}
    </div>
  );
}
